/**
 * JsLibrariesPane (ADR-0046) — the per-app "Librerie JS" manager, opened from
 * the builder rail's BOTTOM (app-global) zone as a main-pane AREA.
 *
 * Layout: **master/detail like Query** (WS-8) — the library list (+ the "Nuova
 * libreria" entry) is the lateral SIDEBAR (`view="list"`), and the CONTENT
 * (`view="detail"`) is either the add form (when "Nuova" is selected) or the
 * selected library's details. Selection is CONTROLLED by the parent so the two
 * mounts agree; omitting `view`/the controlled props renders the legacy single
 * pane (back-compat for the unit test).
 *
 * Lists the app's external JS libraries (`apps.js_libraries`) and adds/removes
 * them. Delivery is RUNTIME-CDN: the backend stores POINTERS only — name +
 * version + global + (derived or pasted) https URL + format (umd|esm) — and the
 * portal/builder load the bundle from that pinned URL at runtime (like
 * Appsmith). Adding a library is metadata-only (no fetch/store). A library's
 * global becomes callable from JS Objects / `run_js` / `{{ }}` bindings;
 * self-hosting the bytes is a deferred hardening.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Package, Plus, Trash2 } from "lucide-react";

import { appsApi, type JsLibraryAdd, type JsLibraryFormat } from "@/lib/api-apps";
import { ApiError } from "@/lib/api";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

const FORMATS: JsLibraryFormat[] = ["umd", "esm"];

/** npm-ish package name (incl. scoped `@scope/pkg`) — mirrors the backend `_NAME_RE`. */
const NAME_RE = /^@?[a-z0-9][a-z0-9._/-]{0,99}$/i;
/** A JS identifier for the global — mirrors the backend `_GLOBAL_RE`. */
const GLOBAL_RE = /^[A-Za-z_$][\w$]*$/;

/** The CDN URL Tray DERIVES from name+version+format when none is pasted —
 *  mirrors the backend `libraries.cdn_url` (UMD → jsdelivr, ESM → esm.sh). */
export function derivedCdnUrl(name: string, version: string, format: JsLibraryFormat): string {
  const n = name.trim();
  const v = version.trim();
  if (!n || !v) return "";
  return format === "esm"
    ? `https://esm.sh/${n}@${v}?bundle`
    : `https://cdn.jsdelivr.net/npm/${n}@${v}`;
}

/** The CDN provider a format derives from (shown in the URL hint). */
const FORMAT_CDN: Record<JsLibraryFormat, string> = { umd: "jsdelivr", esm: "esm.sh" };

export const NEW = "__new__";

type AreaView = "list" | "detail" | "full";

export function JsLibrariesPane({
  appId,
  canWrite = true,
  view = "full",
  selectedName,
  onSelectName,
}: {
  appId: string;
  canWrite?: boolean;
  view?: AreaView;
  /** Controlled selection (master/detail split) — a library name or `NEW`.
   *  Omit for the legacy single pane. */
  selectedName?: string;
  onSelectName?: (name: string) => void;
}) {
  const qc = useQueryClient();
  const librariesQ = useQuery({
    queryKey: ["apps", appId, "js-libraries"],
    queryFn: () => appsApi.listJsLibraries(appId),
  });

  const [form, setForm] = useState<JsLibraryAdd>({ name: "", version: "", global: "", format: "umd" });
  const [showUrl, setShowUrl] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which list entry is open in the detail: a library name, or the add form.
  const controlled = onSelectName !== undefined;
  const [internalSel, setInternalSel] = useState<string>(NEW);
  const selected = controlled ? (selectedName ?? NEW) : internalSel;
  const setSelected = (name: string) => (controlled ? onSelectName!(name) : setInternalSel(name));

  const invalidate = () => qc.invalidateQueries({ queryKey: ["apps", appId, "js-libraries"] });

  const addMut = useMutation({
    mutationFn: (body: JsLibraryAdd) => appsApi.addJsLibrary(appId, body),
    onSuccess: (_d, body) => {
      invalidate();
      setForm({ name: "", version: "", global: "", format: "umd" });
      setShowUrl(false);
      setError(null);
      setSelected(body.name.trim());
    },
    onError: (e) => setError(errMsg(e)),
  });
  const removeMut = useMutation({
    mutationFn: (name: string) => appsApi.removeJsLibrary(appId, name),
    onSuccess: () => {
      invalidate();
      setSelected(NEW);
    },
    onError: (e) => setError(errMsg(e)),
  });

  const libraries = librariesQ.data ?? [];
  const selectedLib = libraries.find((l) => l.name === selected) ?? null;
  const fmt: JsLibraryFormat = form.format ?? "umd";
  const derivedUrl = derivedCdnUrl(form.name, form.version, fmt);
  const badName = form.name !== "" && !NAME_RE.test(form.name);
  const badGlobal = form.global !== "" && !GLOBAL_RE.test(form.global);
  const canAdd =
    canWrite &&
    !!form.name.trim() &&
    !!form.version.trim() &&
    !!form.global.trim() &&
    !badName &&
    !badGlobal &&
    !addMut.isPending;

  const submit = () => {
    if (!canAdd) return;
    const body: JsLibraryAdd = {
      name: form.name.trim(),
      version: form.version.trim(),
      global: form.global.trim(),
      format: form.format,
    };
    if (showUrl && form.url?.trim()) body.url = form.url.trim();
    addMut.mutate(body);
  };

  const masterCls =
    view === "full"
      ? "flex w-56 shrink-0 flex-col gap-1 overflow-y-auto border-r border-border/60 pr-2"
      : "flex h-full flex-col gap-1 overflow-y-auto text-[12px]";

  const master = (
    <div className={masterCls}>
        <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Librerie
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setSelected(NEW)}
            className="mb-1 flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground hover:border-accent/60 hover:text-foreground disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Nuova libreria
          </button>
        )}
        {libraries.length === 0 ? (
          <p className="px-1 py-1 text-[11px] text-muted-foreground">Nessuna libreria.</p>
        ) : (
          libraries.map((lib) => {
            const active = lib.name === selected;
            return (
              <button
                key={lib.name}
                type="button"
                onClick={() => setSelected(lib.name)}
                className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-left ${
                  active ? "bg-accent/15 text-accent" : "hover:bg-surface"
                }`}
              >
                <Package className="h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 flex-1 truncate font-mono">{lib.name}</span>
                <span className="shrink-0 text-[9px] uppercase text-muted-foreground/70">{lib.format}</span>
              </button>
            );
          })
        )}
      </div>
  );

  const detailInner = selectedLib ? (
          <div className="flex max-w-xl flex-col gap-3">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-muted-foreground" />
              <span className="font-mono text-[13px] font-medium text-foreground">{selectedLib.name}</span>
              <span className="text-[11px] text-muted-foreground">@{selectedLib.version}</span>
              <span className="rounded bg-elevated px-1 text-[9px] uppercase text-muted-foreground/80">
                {selectedLib.format}
              </span>
            </div>
            <dl className="flex flex-col gap-2 text-[11px]">
              <div className="flex gap-2">
                <dt className="w-20 shrink-0 text-muted-foreground">Global</dt>
                <dd className="font-mono text-accent">{selectedLib.global}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-20 shrink-0 text-muted-foreground">URL</dt>
                <dd className="min-w-0">
                  <a
                    href={selectedLib.url}
                    target="_blank"
                    rel="noreferrer"
                    className="break-all font-mono text-foreground/80 hover:text-foreground"
                  >
                    {selectedLib.url}
                  </a>
                </dd>
              </div>
            </dl>
            {canWrite && (
              <button
                type="button"
                className="flex items-center gap-1 self-start text-[11px] text-danger hover:underline disabled:opacity-50"
                disabled={removeMut.isPending}
                onClick={() => {
                  if (window.confirm(`Rimuovere la libreria "${selectedLib.name}"?`))
                    removeMut.mutate(selectedLib.name);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" /> Rimuovi libreria
              </button>
            )}
          </div>
        ) : !canWrite ? (
          <div className="flex h-full items-center justify-center text-[11px] text-muted-foreground">
            Seleziona una libreria.
          </div>
        ) : (
          <div className="flex max-w-xl flex-col gap-3">
            <div className="rounded-md border border-border/60 bg-surface/40 px-3 py-2 text-[11px] text-muted-foreground">
              <span className="font-medium text-foreground">Librerie JS</span> — librerie esterne
              (lodash, dayjs, …) caricate dalla CDN al runtime e usabili da JS Objects,{" "}
              <code className="font-mono">run_js</code> e i binding <code className="font-mono">{`{{ }}`}</code>.
              Bloccate a una versione; l'URL si deriva da nome+versione (o incollalo).
            </div>

            <div className="flex flex-wrap items-center gap-1">
              <input
                aria-label="Nome pacchetto"
                className={`w-40 rounded border bg-background px-1.5 py-1 font-mono ${badName ? "border-danger" : "border-border"}`}
                placeholder="lodash"
                title={badName ? "Nome pacchetto non valido" : undefined}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
              <input
                aria-label="Versione"
                className="w-28 rounded border border-border bg-background px-1.5 py-1 font-mono"
                placeholder="4.17.21"
                value={form.version}
                onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))}
              />
              <input
                aria-label="Global"
                className={`w-28 rounded border bg-background px-1.5 py-1 font-mono ${badGlobal ? "border-danger" : "border-border"}`}
                placeholder="_ (global)"
                title={badGlobal ? "Identificatore non valido" : "Il global window (UMD) / specifier (ESM)"}
                value={form.global}
                onChange={(e) => setForm((f) => ({ ...f, global: e.target.value }))}
              />
              {/* Segmented UMD|ESM — clearer than a native combobox. */}
              <div
                role="radiogroup"
                aria-label="Formato"
                className="inline-flex overflow-hidden rounded border border-border"
              >
                {FORMATS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    role="radio"
                    aria-checked={fmt === opt}
                    className={`px-2 py-1 text-[11px] font-medium ${
                      fmt === opt
                        ? "bg-accent text-accent-foreground"
                        : "bg-background text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => setForm((f) => ({ ...f, format: opt }))}
                  >
                    {opt.toUpperCase()}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="rounded bg-accent px-2.5 py-1 text-[11px] font-medium text-accent-foreground disabled:opacity-50"
                onClick={submit}
                disabled={!canAdd}
              >
                Aggiungi
              </button>
            </div>

            {/* What URL gets used — DERIVED live unless a custom one is pasted. */}
            <div className="flex items-start gap-1.5 text-[10px]">
              <span className="shrink-0 pt-px font-semibold uppercase tracking-wider text-muted-foreground/70">
                URL
              </span>
              {showUrl && form.url?.trim() ? (
                <span className="min-w-0 break-all font-mono text-foreground/80">
                  {form.url.trim()} <span className="text-muted-foreground/60">(personalizzato)</span>
                </span>
              ) : derivedUrl ? (
                <span className="min-w-0 break-all font-mono text-foreground/80">
                  {derivedUrl} <span className="text-muted-foreground/60">(derivato da {FORMAT_CDN[fmt]})</span>
                </span>
              ) : (
                <span className="text-muted-foreground/60">
                  inserisci nome + versione e Tray deriva l'URL da {FORMAT_CDN[fmt]}.
                </span>
              )}
            </div>

            <div>
              <button
                type="button"
                className="text-[10px] text-muted-foreground hover:text-foreground"
                onClick={() => setShowUrl((s) => !s)}
              >
                {showUrl ? "− URL personalizzato" : "+ URL personalizzato (avanzato)"}
              </button>
              {showUrl && (
                <input
                  aria-label="URL CDN"
                  className="mt-1 w-full rounded border border-border bg-background px-1.5 py-1 font-mono text-[11px]"
                  placeholder="https://cdn.jsdelivr.net/npm/lodash@4.17.21 (lascia vuoto per derivarlo)"
                  value={form.url ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                />
              )}
            </div>

            {error && <p className="text-[11px] text-danger">{error}</p>}
          </div>
  );

  if (view === "list") return master;
  if (view === "detail")
    return <div className="h-full overflow-y-auto text-[12px]">{detailInner}</div>;

  return (
    <div className="flex h-full gap-3 text-[12px]">
      {master}
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">{detailInner}</div>
    </div>
  );
}
